import {useLayoutEffect,useRef,useState,type CSSProperties,type ReactNode,type RefObject} from 'react';
import {createPortal} from 'react-dom';

type TuningBackdropGeometry={x:number;y:number;start:number;anchorX:number;anchorY:number};
type PopoverPlacement={left:number;top:number;ready:boolean;backdrop:TuningBackdropGeometry|null};

function layoutOffset(element:HTMLElement,ancestor:HTMLElement){
 let x=0,y=0,current:HTMLElement|null=element;
 while(current&&current!==ancestor){x+=current.offsetLeft;y+=current.offsetTop;current=current.offsetParent as HTMLElement|null;}
 return current===ancestor?{x,y}:null;
}

/** A native-style popover above its source control. The dismissal shield lives
 * above reader/transcript iframes, whose pointer events cannot bubble to React. */
export function ComposerPopover({title,anchor,width=360,kind,children,onClose}: {
 title:string;anchor:RefObject<HTMLElement|null>;width?:number;kind:string;children:ReactNode;onClose:()=>void;
}){
 const panel=useRef<HTMLDivElement>(null),tuningGeometry=useRef<TuningBackdropGeometry|null>(null);
 const [placement,setPlacement]=useState<PopoverPlacement>({left:12,top:12,ready:false,backdrop:null});
 useLayoutEffect(()=>{
  const position=()=>{const rect=anchor.current?.getBoundingClientRect(),box=panel.current?.getBoundingClientRect();if(!box)return;
   const centered=kind==='model'||kind==='verbosity';
   const left=rect&&centered?rect.left+(rect.width-box.width)/2:(rect?.right??window.innerWidth)-box.width;
   const placedLeft=Math.max(12,Math.min(left,window.innerWidth-box.width-12)),top=Math.max(12,(rect?.top??window.innerHeight)-box.height-12);
   const anchorX=rect?rect.left+rect.width/2:window.innerWidth/2,anchorY=rect?.top??window.innerHeight;
   let backdrop:TuningBackdropGeometry|null=null;
   if(centered){
    // Layout offsets stay stable while the rail's opening transform animates.
    const host=panel.current!,header=host.querySelector<HTMLElement>('.chat-ipad-tuning-header'),rail=host.querySelector<HTMLElement>('.chat-ipad-tuning-rail');
    const headerOffset=header&&layoutOffset(header,host),railOffset=rail&&layoutOffset(rail,host);
    backdrop={x:placedLeft+(rail&&railOffset?railOffset.x+rail.offsetWidth/2:box.width/2),y:top+(rail&&railOffset?railOffset.y+rail.offsetHeight/2:box.height/2),start:top+(headerOffset?.y??14)-32,anchorX,anchorY};
   }else if(kind==='models'){
    // The native model menu sits over the existing tuning backdrop. Keep that
    // backdrop in place when this host replaces the rail with its model list.
    const previous=tuningGeometry.current;
    if(previous)backdrop={x:previous.x+anchorX-previous.anchorX,y:previous.y+anchorY-previous.anchorY,start:previous.start+anchorY-previous.anchorY,anchorX,anchorY};
    else{
     // Match the 352×133 tuning layout (14 padding, 29 title, 18 gap, 58 rail)
     // above this control, independent of the model list's content height.
     const halfWidth=Math.min(352,window.innerWidth-24)/2;
     backdrop={x:Math.max(12+halfWidth,Math.min(anchorX,window.innerWidth-halfWidth-12)),y:anchorY-55,start:anchorY-163,anchorX,anchorY};
    }
   }
   tuningGeometry.current=backdrop;
   setPlacement({left:placedLeft,top,ready:true,backdrop});};
  position();const observer=new ResizeObserver(position);
  // Opening tuning expands the composer; pane resizing can also move the
  // fixed-size trigger without changing the popover's own dimensions.
  for(const element of [panel.current,anchor.current,anchor.current?.closest('.chat-ipad-composer-host')])if(element)observer.observe(element);
  window.addEventListener('resize',position);window.addEventListener('scroll',position,true);
  return()=>{observer.disconnect();window.removeEventListener('resize',position);window.removeEventListener('scroll',position,true);};
 },[anchor,kind]);
 useLayoutEffect(()=>{if(placement.ready)panel.current?.focus({preventScroll:true});},[kind,placement.ready]);
 const backdropStyle=placement.backdrop?{'--tuning-backdrop-x':`${placement.backdrop.x}px`,'--tuning-backdrop-y':`${placement.backdrop.y}px`,'--tuning-backdrop-start':`${placement.backdrop.start}px`,visibility:placement.ready?'visible':'hidden'} as CSSProperties:undefined;
 return createPortal(<div className="chat-ipad-popover-layer">
  <button type="button" className="chat-ipad-popover-dismiss" aria-label="关闭输入框面板" onClick={onClose}>
   {backdropStyle&&<span className="chat-ipad-tuning-backdrop" style={backdropStyle} aria-hidden="true">
    <span className="chat-ipad-tuning-backdrop-ultrathin"/><span className="chat-ipad-tuning-backdrop-thin"/><span className="chat-ipad-tuning-backdrop-regular"/><span className="chat-ipad-tuning-backdrop-tint"/>
   </span>}
  </button>
  <div ref={panel} tabIndex={-1} className={`chat-ipad-popover chat-ipad-popover-${kind}`} role="dialog" aria-label={title}
   style={{width,maxWidth:'calc(100vw - 24px)',left:placement.left,top:placement.top,visibility:placement.ready?'visible':'hidden'}}
   onKeyDown={event=>{if(event.key==='Escape'){event.preventDefault();event.stopPropagation();onClose();}}}>

   {children}
  </div>
 </div>,document.body);
}
